/**
 * CONTROL NETWORKS - who is somebody else's to look after.
 *
 * A sergeant and his servitors; a magos and her skitarii; a player who has
 * brought four hounds. The app has never had a way to say that, so a reaction
 * owed by a servitor rings for nobody in particular, and picking one up means
 * hunting the roster for it.
 *
 * WHAT A NETWORK IS NOT. It is not ownership and it is not authority. Nothing
 * in this file gates an action: any player may still act as any combatant and
 * answer any reaction, exactly as before. The user rejected an ownership model
 * outright, and this is deliberately the weaker thing - a network does two
 * jobs and no more:
 *
 *   1. it raises the ALARM for a controller when a subordinate is attacked
 *   2. it groups units in a squad bar, so switching between them is one click
 *
 * THE FAILURE MODE IS SILENCE. A tag typed slightly wrong, or a controller who
 * is not in this session, raises nobody and looks exactly like nothing being
 * wrong. So the roster shows the network and its controller (C2), and the
 * quiet entry still reaches everybody regardless of any of this.
 *
 * The record lives on the SHEET - `character.control` - so a hierarchy travels
 * with the characters through export, import and the library, rather than
 * being re-established per session. It is optional and never defaulted:
 * writing a field into every stored character would rewrite them all once and
 * churn the cold-write path for nothing.
 *
 * Pure, zero React dependency.
 */
import type { Combatant } from './types';
import type { Character } from '../character/types';

export type ControlInfo = NonNullable<Character['control']>;

export interface ControlNetwork {
  tag: string;
  label: string;
  /** The combatant flagged as controller, or null for an orphaned network. */
  controller: Combatant | null;
  /** Everyone carrying the tag, controller included, in roster order. */
  members: Combatant[];
}

/**
 * This combatant's control record, or null.
 *
 * A blank tag is NOT a network. A half-filled Control tab - a label typed, no
 * tag minted - is the likeliest state this feature will ever be in, and
 * treating it as a real network would silently group every such character.
 */
export function controlOf(combatant: Combatant | null | undefined): ControlInfo | null {
  const control = combatant?.sheet?.control;
  if (!control) return null;
  return control.tag && control.tag.trim() ? control : null;
}

/**
 * Everyone carrying a tag.
 *
 * An absent tag matches NOBODY, which needs saying: `control?.tag === tag`
 * with both sides undefined is true for every ordinary character on the
 * roster, and that would put the whole session in one squad.
 */
export function networkMembers(roster: readonly Combatant[], tag: string | null | undefined): Combatant[] {
  if (!tag || !tag.trim()) return [];
  return roster.filter((c) => controlOf(c)?.tag === tag);
}

/** The combatant who holds the network, or null when they are not here. */
export function controllerFor(roster: readonly Combatant[], tag: string | null | undefined): Combatant | null {
  if (!tag || !tag.trim()) return null;
  return roster.find((c) => controlOf(c)?.tag === tag && controlOf(c)?.isController) ?? null;
}

/**
 * The squad a combatant belongs to, if it is really a squad.
 *
 * A tag only one combatant carries is a typo or a leftover from a deleted
 * unit, not a network - and offering a squad bar with one button in it would
 * advertise the mistake as a feature.
 */
export function networkOfActor(
  roster: readonly Combatant[],
  actorId: string | null | undefined,
): ControlNetwork | null {
  if (!actorId) return null;
  const control = controlOf(roster.find((c) => c.id === actorId));
  if (!control?.tag) return null;
  const members = networkMembers(roster, control.tag);
  if (members.length < 2) return null;
  return {
    tag: control.tag,
    label: control.label || control.tag,
    controller: controllerFor(roster, control.tag),
    members,
  };
}

/**
 * IS THIS COMBATANT MINE TO WATCH? The single question the alarm asks.
 *
 * True only when the combatant I am acting as is the CONTROLLER of the
 * network the attacked combatant belongs to. Not between peers - a network is
 * a hierarchy, not a party line, and every servitor's client shouting at every
 * other servitor's attack is the noise this feature would otherwise add.
 *
 * False for the controller about themselves: their own reaction is already
 * loud because it IS their actor, and going through the network as well would
 * say nothing new.
 */
export function isControlledBy(
  roster: readonly Combatant[],
  combatantId: string,
  actorId: string | null | undefined,
): boolean {
  if (!actorId || actorId === combatantId) return false;
  const tag = controlOf(roster.find((c) => c.id === combatantId))?.tag;
  if (!tag) return false;
  return controllerFor(roster, tag)?.id === actorId;
}

/**
 * Mint a tag from a unique part supplied by the caller.
 *
 * The uniqueness comes from OUTSIDE. This is not the dice, and engine code
 * does not reach for randomness (gotcha #5) - the editor passes an id minted
 * the same way the rest of the app mints them.
 */
export function controlTag(unique: string): string | null {
  const trimmed = (unique || '').trim();
  return trimmed ? `net-${trimmed}` : null;
}
